import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { isAfter, isBefore, isSameDay, isValid } from "date-fns";
import FocusTrap from "focus-trap-react";
import { useStringMap } from "../../utilities/string-map/useStringMap";
import { useDateField } from "../date-picker/useDateField";
import { CalendarFooter } from "../date-calendar/CalendarFooter";
import { DateCalendar } from "../date-calendar/DateCalendar";
import { Popper } from "../../popper/Popper";
import { PopperBox } from "../../popper/PopperBox";
import { useCombineRefs } from "../../hooks/useCombineRefs";
import { useUniqueId } from "../../hooks/useUniqueId";
import { useViewportWidth } from "../../hooks/useViewportWidth";
import { ScreenReaderContent } from "../../screen-reader-content/ScreenReaderContent";
import { stringMapDefault as datePickerStringMapDefault } from "../date-picker/DatePicker.stringMap";
import * as Styled from "./DateRangePicker.styled";
import { defaults } from "./DateRangePicker.defaults";
import { useInViewMonth } from "./useInViewMonth";
import { DateRangePickerProvider } from "./DateRangePickerContext";
import { stringMapDefault } from "./DateRangePicker.stringMap";
const getSelectedRange = (startDate, endDate) => {
    startDate = isValid(startDate) ? startDate : null;
    endDate = isValid(endDate) ? endDate : null;
    return startDate || endDate ? { from: startDate || endDate || undefined, to: endDate || undefined } : undefined;
};
const componentName = "DateRangePicker";
export const DateRangePicker = forwardRef((props, ref) => {
    const { start: startProp, end: endProp, stringMap: stringMapProp, onDateRangeChange, shouldValidateOnBlur = defaults.shouldValidateOnBlur, dateFormat = defaults.dateFormat, calendarProps = defaults.calendarProps, numberOfMonths: numberOfMonthsProp, calendarDisplay = "popover", disabled, locale, ...rest } = props;
    // @ts-expect-error -- TODO: design a better API for components with third-party subcomponents
    const stringMap = useStringMap(componentName, { stringMapDefault, stringMapProp });
    const datePickerStringMap = useStringMap("DatePicker", {
        // @ts-expect-error -- TODO: design a better API for components with third-party subcomponents
        stringMapDefault: datePickerStringMapDefault,
        stringMapProp: stringMapProp
    });
    const { toggleCalendar, closeCalendar, invalidDate, calendarDescription, selectingStartDate, selectingEndDate, errorStartAfterEnd, errorEndBeforeStart, ...labels } = { ...datePickerStringMap, ...stringMap };
    const { onDayClick, ...dayPickerProps } = calendarProps;
    const selectingDescriptionId = useUniqueId("sd");
    const pickerRef = useRef(null);
    const calendarRef = useRef(null);
    const viewportWidth = useViewportWidth();
    const numberOfMonths = numberOfMonthsProp ?? (viewportWidth > 680 ? 2 : 1);
    const start = useMemo(() => ({ ...defaults.start, ...startProp }), [startProp]);
    const end = useMemo(() => ({ ...defaults.end, ...endProp }), [endProp]);
    const [isOpen, setIsOpen] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const [focusedField, setFocusedField] = useState("start");
    const [month, setMonth] = useInViewMonth({
        startDate: start.date,
        endDate: end.date,
        focusedField,
        numberOfMonths
    });
    useEffect(() => {
        calendarRef.current?.querySelector(`rdp-day_range_${focusedField}`)?.focus();
    }, [focusedField]);
    const fieldProps = {
        isFullWidth: true,
        dateFormat,
        shouldValidateOnBlur,
        stringMap: { toggleCalendar, invalidDate, calendarDescription, closeCalendar },
        isToggleLabelImplicit: start.stringMap?.toggleCalendar !== end.stringMap?.toggleCalendar,
        disabled,
        onKeyDown: (e) => {
            // close on escape
            if (e.key === "Escape" && isOpen) {
                setIsOpen(false);
                // Prevent escape from bubbling and closing other popovers or dialogs
                e.stopPropagation();
            }
            if (e.key === " " && !isOpen) {
                setIsOpen(true);
                e.preventDefault();
            }
        }
    };
    const startField = useDateField({
        hasFocus: hasFocus && focusedField === "start",
        onFocus: () => setFocusedField("start"),
        calendarRef,
        validate: ({ date }) => 
        // start cannot be after end
        end.date && date && isAfter(date, end.date) ? errorStartAfterEnd : "",
        hasPopover: calendarDisplay === "popover",
        onCalendarButtonPress: calendarDisplay === "popover" ? () => setFocusedField("start") : undefined,
        ...fieldProps,
        ...start,
        onDateChange: (startDate) => {
            start.onDateChange(startDate);
            onDateRangeChange?.(startDate, end.date);
        },
        stringMap: {
            ...fieldProps.stringMap,
            toggleCalendar: (start.stringMap?.toggleCalendar || toggleCalendar)
        }
    });
    const endField = useDateField({
        hasFocus: hasFocus && focusedField === "end",
        onFocus: () => setFocusedField("end"),
        calendarRef,
        hasPopover: calendarDisplay === "popover",
        validate: ({ date }) => 
        // end cannot be before start
        start.date && date && isBefore(date, start.date) ? errorEndBeforeStart : "",
        onCalendarButtonPress: calendarDisplay === "popover" ? () => setFocusedField("end") : undefined,
        ...fieldProps,
        ...end,
        onDateChange: (endDate) => {
            end.onDateChange(endDate);
            onDateRangeChange?.(start.date, endDate);
        },
        stringMap: {
            ...fieldProps.stringMap,
            toggleCalendar: (end.stringMap?.toggleCalendar || toggleCalendar)
        }
    });
    const activeField = focusedField === "end" ? endField : startField;
    const calendar = calendarDisplay === "none" ? null : (_jsx(DateCalendar, { ref: calendarRef, initialFocus: false, selected: getSelectedRange(start.date, end.date), locale: locale, labels: labels, mode: "range", month: month, onMonthChange: setMonth, numberOfMonths: numberOfMonths, modifiers: {
            focused: (focusedField === "start" ? start.date : end.date) || false
        }, modifiersClassNames: { focused: "rdp-day_focused" }, onDayClick: (date, modifiers, e) => {
            onDayClick?.(date, modifiers, e);
            if (modifiers.disabled) {
                return;
            }
            if (focusedField === "start") {
                if (start.date && isSameDay(date, start.date)) {
                    start.onDateChange(null);
                    onDateRangeChange?.(null, end.date);
                    return;
                }
                const startHasChanged = !start.date || !isValid(start.date) || !isSameDay(date, start.date);
                if (startHasChanged) {
                    start.onDateChange(date);
                    startField.setValidationMessage("");
                    if (!end.date) {
                        setFocusedField("end");
                        onDateRangeChange?.(date, null);
                        return;
                    }
                    if (isAfter(date, end.date)) {
                        end.onDateChange(null);
                        endField.setValidationMessage("");
                        setFocusedField("end");
                        onDateRangeChange?.(date, null);
                    }
                    else {
                        onDateRangeChange?.(date, end.date);
                    }
                }
                return;
            }
            /* focusedField === "end" */
            if (end.date && isSameDay(date, end.date)) {
                end.onDateChange(null);
                onDateRangeChange?.(start.date, null);
                return;
            }
            const endHasChanged = !end.date || !isValid(end.date) || !isSameDay(date, end.date);
            if (endHasChanged) {
                end.onDateChange(date);
                endField.setValidationMessage("");
                if (!start.date) {
                    setFocusedField("start");
                    onDateRangeChange?.(null, date);
                    return;
                }
                if (isBefore(date, start.date)) {
                    start.onDateChange(date);
                    onDateRangeChange?.(date, date);
                }
                else {
                    onDateRangeChange?.(start.date, date);
                }
            }
        }, ...dayPickerProps }));
    return (_jsx(Styled.DateRangePicker, { "data-testid": "DateRangePicker", onFocus: () => setHasFocus(true), onBlur: (e) => {
            if (e.currentTarget.contains(e.relatedTarget) || calendarRef.current?.contains(e.relatedTarget)) {
                return;
            }
            setHasFocus(false);
        }, ref: useCombineRefs(pickerRef, ref), ...rest, children: _jsx(DateRangePickerProvider, { startDate: start.date, endDate: end.date, startLabelId: startField.labelId, endLabelId: endField.labelId, setFocusedField: setFocusedField, selectingDescriptionId: selectingDescriptionId, children: _jsxs(_Fragment, { children: [startField.dateField, endField.dateField, calendarDisplay === "popover" ? (_jsx(Popper, { isOpen: isOpen, onCloseCallback: (e) => {
                            if (
                            // Keep open start/end if inputs are clicked
                            e?.type === "click" &&
                                (e.target === startField.inputRef.current || e.target === endField.inputRef.current)) {
                                return;
                            }
                            setIsOpen(false);
                        }, placement: "bottom", fallbackPlacements: ["top", "bottom-start", "top-start"], popoverOptions: { mode: "manual", returnFocusRef: activeField.inputRef }, placementRef: pickerRef, triggerRef: activeField.calendarButtonRef, onOpenCallback: () => {
                            setIsOpen(true);
                        }, onOpened: () => {
                            const calendar = calendarRef.current;
                            // Focus active range end, or first day if none selected.
                            const focusTarget = calendar?.querySelector(`.rdp-day_range_${focusedField}`) ||
                                calendar?.querySelector(`.rdp-day_selected`) ||
                                calendar?.querySelector(`.rdp-day`);
                            focusTarget?.focus();
                        }, onClose: () => {
                            // Focus active field on calendar close
                            activeField.inputRef.current?.focus();
                        }, overlay: ({ isOpen }) => (_jsx(FocusTrap, { active: isOpen, focusTrapOptions: {
                                clickOutsideDeactivates: true,
                                returnFocusOnDeactivate: false,
                                initialFocus: false,
                                // https://github.com/focus-trap/focus-trap-react#testing-in-jsdom
                                tabbableOptions: { displayCheck: "none" }
                            }, children: _jsx(Styled.DateRangePickerPopperWrapper, { "data-testid": "DateRangePickerPopperBoxWrapper", children: _jsxs(PopperBox, { maxWidth: "none", hasPadding: false, role: "dialog", "aria-label": calendarDescription, children: [calendar, _jsx(CalendarFooter, { stringMap: { closeCalendar: closeCalendar }, onClose: () => {
                                                setIsOpen(false);
                                            }, children: _jsx(ScreenReaderContent, { id: selectingDescriptionId, "aria-hidden": "true", children: focusedField === "start" ? selectingStartDate : selectingEndDate }) })] }) }) })) })) : (calendar)] }) }) }));
});
DateRangePicker.displayName = componentName;
